import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download, Eye, FileText, Loader2, MapPin, Paperclip, User, X } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { bitacoraPhotoUrl, fetchPhotoMeta, type PhotoMeta } from '@/features/mantenimiento/api';
import { authHeaders } from '@/shared/api/client';

/**
 * "Ver Detalles del Despacho" — read-only popup on the Despacho stage
 * card. Mirrors PowerApps's `Group_VerDespacho_DV`: recipient name, full
 * dispatch address, observations, and the comprobante photos uploaded
 * at confirmation time.
 *
 * Photos are stored as drive-item ids on `DespachoFotoIds_RO`. The
 * `/api/mantenimiento/photo/:driveItemId` endpoint requires a bearer
 * token, but `<img src>` doesn't carry our `Authorization` header — so
 * we list each comprobante as a chip with explicit preview / download
 * actions that fetch the bytes through `fetch()` and reconstitute as
 * blob URLs.
 */
export function DespachoDetallesDialog({
  open,
  onClose,
  recibe,
  direccion,
  observaciones,
  fotoIdsJson,
  fotoTiposJson,
}: {
  open: boolean;
  onClose: () => void;
  recibe: string | null | undefined;
  direccion: string | null | undefined;
  observaciones: string | null | undefined;
  /** Raw JSON-encoded array of drive item ids on `DespachoFotoIds_RO`. */
  fotoIdsJson: string | null | undefined;
  /** Raw JSON-encoded array of file-type labels on
   *  `DespachoFotoTipos_RO`. Parallel to `fotoIdsJson`; legacy rows
   *  without this column fall through as null and we render no badge. */
  fotoTiposJson?: string | null | undefined;
}) {
  const fotoIds = parseFotoIds(fotoIdsJson);
  const fotoTipos = parseFotoTipos(fotoTiposJson);
  const empty =
    !recibe?.trim() &&
    !direccion?.trim() &&
    !observaciones?.trim() &&
    fotoIds.length === 0;
  const [previewIdx, setPreviewIdx] = useState<number | null>(null);
  // Fetch the per-photo metadata (filename, mime) on open so the rows
  // can render real names instead of "Comprobante N" placeholders. The
  // server pulls these straight from Microsoft Graph — no SharePoint
  // schema change needed.
  const metaQ = useQuery({
    queryKey: ['despacho', 'photo-meta', fotoIds],
    queryFn: () => Promise.all(fotoIds.map((id) => fetchPhotoMeta(id))),
    enabled: open && fotoIds.length > 0,
    staleTime: 5 * 60_000,
  });
  const metas: Array<PhotoMeta | null> =
    metaQ.data ?? fotoIds.map(() => null);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Detalles del Despacho</DialogTitle>
          <DialogDescription>
            Información cargada al confirmar el despacho.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4">
          {empty ? (
            <div className="flex flex-col items-center justify-center gap-2 px-4 py-12 text-sm text-muted-foreground">
              <FileText className="h-6 w-6" aria-hidden />
              Esta venta todavía no tiene un despacho confirmado.
            </div>
          ) : (
            <>
              <div className="grid gap-3 sm:grid-cols-2">
                <Field
                  icon={<User className="h-4 w-4" aria-hidden />}
                  label="Recibe"
                  value={recibe?.trim() || '—'}
                />
                <Field
                  icon={<MapPin className="h-4 w-4" aria-hidden />}
                  label="Dirección"
                  value={direccion?.trim() || '—'}
                />
              </div>
              {observaciones?.trim() && (
                <div className="space-y-1.5">
                  <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                    Observaciones
                  </p>
                  <p className="whitespace-pre-line rounded-lg border bg-secondary/40 px-3 py-2 text-sm text-foreground">
                    {observaciones}
                  </p>
                </div>
              )}
              {fotoIds.length > 0 && (
                <div className="space-y-1.5">
                  <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                    Comprobantes ({fotoIds.length})
                  </p>
                  <ul className="divide-y rounded-lg border bg-card">
                    {fotoIds.map((id, i) => (
                      <ComprobanteRow
                        key={id}
                        index={i}
                        driveItemId={id}
                        fileName={metas[i]?.fileName ?? null}
                        tipo={fotoTipos[i] ?? null}
                        loading={metaQ.isPending}
                        onPreview={() => setPreviewIdx(i)}
                      />
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cerrar
          </Button>
        </DialogFooter>
        {previewIdx != null && fotoIds[previewIdx] && (
          <ComprobantePreviewDialog
            index={previewIdx}
            driveItemId={fotoIds[previewIdx]}
            fileName={metas[previewIdx]?.fileName ?? null}
            onClose={() => setPreviewIdx(null)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function ComprobanteRow({
  index,
  driveItemId,
  fileName,
  tipo,
  loading,
  onPreview,
}: {
  index: number;
  driveItemId: string;
  fileName: string | null;
  /** File-type badge ("Remito" / "DNI Frente" / etc.). null means
   *  the row is from a legacy despacho confirmed before the tipo
   *  column existed — render no badge. */
  tipo: string | null;
  loading: boolean;
  onPreview: () => void;
}) {
  const [downloading, setDownloading] = useState(false);
  const displayName = fileName ?? `Comprobante ${index + 1}`;
  const handleDownload = async () => {
    setDownloading(true);
    try {
      const { url, mimeType } = await fetchPhoto(driveItemId);
      const fallback = `comprobante-despacho-${index + 1}${extensionFromMime(mimeType)}`;
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName || fallback;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      toast.error('No se pudo descargar el comprobante', {
        description: err instanceof Error ? err.message : 'Error desconocido',
      });
    } finally {
      setDownloading(false);
    }
  };
  return (
    <li className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
      <span className="flex min-w-0 items-center gap-2 text-foreground">
        {tipo && (
          <span className="shrink-0 rounded bg-secondary px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-foreground">
            {tipo}
          </span>
        )}
        <Paperclip className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
        <span className="truncate" title={displayName}>
          {loading && !fileName ? `Comprobante ${index + 1}…` : displayName}
        </span>
      </span>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={onPreview}
          title="Vista previa"
          aria-label={`Vista previa comprobante ${index + 1}`}
          className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
        >
          <Eye className="h-4 w-4" aria-hidden />
        </button>
        <button
          type="button"
          onClick={handleDownload}
          disabled={downloading}
          title="Descargar"
          aria-label={`Descargar comprobante ${index + 1}`}
          className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground disabled:opacity-50"
        >
          {downloading ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          ) : (
            <Download className="h-4 w-4" aria-hidden />
          )}
        </button>
      </div>
    </li>
  );
}

function ComprobantePreviewDialog({
  index,
  driveItemId,
  fileName,
  onClose,
}: {
  index: number;
  driveItemId: string;
  fileName: string | null;
  onClose: () => void;
}) {
  const [src, setSrc] = useState<string | null>(null);
  const [mimeType, setMimeType] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let createdUrl: string | null = null;
    (async () => {
      try {
        const { url, mimeType: mt } = await fetchPhoto(driveItemId);
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        createdUrl = url;
        setSrc(url);
        setMimeType(mt);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : 'Error desconocido');
      }
    })();
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [driveItemId]);

  const isImage = mimeType.startsWith('image/');
  const isPdf = mimeType === 'application/pdf';
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="truncate">
            {fileName ?? `Comprobante ${index + 1}`}
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          {error ? (
            <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
              No se pudo cargar el comprobante. {error}
            </div>
          ) : !src ? (
            <div className="flex h-64 items-center justify-center text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
            </div>
          ) : isImage ? (
            <img
              src={src}
              alt={fileName ?? `Comprobante ${index + 1}`}
              className="mx-auto max-h-[70vh] w-auto rounded-lg"
            />
          ) : isPdf ? (
            <iframe
              src={src}
              title={fileName ?? `Comprobante ${index + 1}`}
              className="h-[70vh] w-full rounded-lg border"
            />
          ) : (
            <div className="flex h-48 items-center justify-center text-sm text-muted-foreground">
              No se puede previsualizar este tipo de archivo. Usá Descargar.
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} className="gap-2">
            <X className="h-4 w-4" aria-hidden />
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="space-y-1.5">
      <p className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {icon}
        {label}
      </p>
      <p className="rounded-lg border bg-secondary/40 px-3 py-2 text-sm text-foreground">
        {value}
      </p>
    </div>
  );
}

async function fetchPhoto(driveItemId: string): Promise<{
  url: string;
  mimeType: string;
}> {
  const res = await fetch(bitacoraPhotoUrl(driveItemId), {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const blob = await res.blob();
  return {
    url: URL.createObjectURL(blob),
    mimeType: res.headers.get('content-type') ?? 'application/octet-stream',
  };
}

function extensionFromMime(mime: string): string {
  if (mime === 'image/jpeg') return '.jpg';
  if (mime === 'image/png') return '.png';
  if (mime === 'image/webp') return '.webp';
  if (mime === 'image/gif') return '.gif';
  if (mime === 'application/pdf') return '.pdf';
  return '';
}

function parseFotoIds(raw: string | null | undefined): string[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((v): v is string => typeof v === 'string' && v.length > 0);
  } catch {
    return [];
  }
}

function parseFotoTipos(raw: string | null | undefined): Array<string | null> {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // Allow null gaps so legacy uploads in mid-stream still index
    // correctly against fotoIds (the parallel array).
    return parsed.map((v) => (typeof v === 'string' && v.length > 0 ? v : null));
  } catch {
    return [];
  }
}
